import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import simnetLogo from "@/assets/simnet-logo-optimized.webp";

const fieldSolutionsMenu = [
  { name: "Field Tablets & Mobile Internet", path: "/field-tablets-mobile-internet" },
  { name: "Fleet & Asset Tracking", path: "/fleet-asset-tracking" },
  { name: "Jobsite Connectivity", path: "/jobsite-connectivity" },
  { name: "Push-to-Talk", path: "/push-to-talk" },
];

const enterpriseMenu = [
  { name: "Business Internet", path: "/business-internet-solutions" },
  { name: "Failover Internet", path: "/failover-solutions" },
  { name: "Fixed Wireless Internet", path: "/fixed-wireless-solutions" },
  { name: "IoT Connectivity", path: "/internet-of-things-iot" },
  { name: "POTS Line Replacement", path: "/pots-replacement-solutions" },
  { name: "Starlink Business", path: "/starlink-business-internet" },
];

export const Navigation = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [fieldOpen, setFieldOpen] = useState(false);
  const [enterpriseOpen, setEnterpriseOpen] = useState(false);
  const [mobileFieldOpen, setMobileFieldOpen] = useState(false);
  const [mobileEnterpriseOpen, setMobileEnterpriseOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-card border-b">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center">
            <img
              src={simnetLogo}
              alt="SimNet Wireless"
              className="h-16"
              width="102"
              height="64"
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" className={`nav-link font-medium ${isActive('/') ? 'text-primary' : ''}`}>Home</Link>

            {/* Field Solutions Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setFieldOpen(true)}
              onMouseLeave={() => setFieldOpen(false)}
            >
              <button className="nav-link font-medium flex items-center gap-1">
                Field Solutions <ChevronDown className="h-4 w-4" />
              </button>
              {fieldOpen && (
                <div className="absolute top-full left-0 mt-0 w-72 glass-card border p-2 animate-fade-in z-50 bg-background/95 backdrop-blur-xl">
                  {fieldSolutionsMenu.map((item) => (
                    <Link
                      key={item.name}
                      to={item.path}
                      className={`block px-4 py-2.5 text-sm nav-link rounded-lg hover:bg-secondary ${isActive(item.path) ? 'text-primary' : ''}`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Enterprise Connectivity Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setEnterpriseOpen(true)}
              onMouseLeave={() => setEnterpriseOpen(false)}
            >
              <button className="nav-link font-medium flex items-center gap-1">
                Enterprise Connectivity <ChevronDown className="h-4 w-4" />
              </button>
              {enterpriseOpen && (
                <div className="absolute top-full left-0 mt-0 w-64 glass-card border p-2 animate-fade-in z-50 bg-background/95 backdrop-blur-xl">
                  {enterpriseMenu.map((item) => (
                    <Link
                      key={item.name}
                      to={item.path}
                      className={`block px-4 py-2.5 text-sm nav-link rounded-lg hover:bg-secondary ${isActive(item.path) ? 'text-primary' : ''}`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/all-solutions" className={`nav-link font-medium ${isActive('/all-solutions') ? 'text-primary' : ''}`}>All Solutions</Link>
            <Link to="/home-internet" className={`nav-link font-medium ${isActive('/home-internet') ? 'text-primary' : ''}`}>Home Internet</Link>

            <a
              href="tel:+15128595047"
              className="flex items-center gap-2 px-4 py-2 rounded-full font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shadow-sm"
              aria-label="Call SimNet at (512) 859-5047"
            >
              <Phone className="h-4 w-4" />
              (512) 859-5047
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden py-4 space-y-2 animate-fade-in">
            <Link to="/" className={`block py-2 nav-link ${isActive('/') ? 'text-primary' : ''}`}>Home</Link>

            <div className="space-y-1">
              <button
                className="w-full text-left py-2 nav-link flex items-center justify-between"
                onClick={() => setMobileFieldOpen(!mobileFieldOpen)}
              >
                Field Solutions <ChevronDown className={`h-4 w-4 transition-transform ${mobileFieldOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileFieldOpen && (
                <div className="pl-4 space-y-1">
                  {fieldSolutionsMenu.map((item) => (
                    <Link key={item.name} to={item.path} className={`block py-2 text-sm nav-link ${isActive(item.path) ? 'text-primary' : ''}`}>
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-1">
              <button
                className="w-full text-left py-2 nav-link flex items-center justify-between"
                onClick={() => setMobileEnterpriseOpen(!mobileEnterpriseOpen)}
              >
                Enterprise Connectivity <ChevronDown className={`h-4 w-4 transition-transform ${mobileEnterpriseOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileEnterpriseOpen && (
                <div className="pl-4 space-y-1">
                  {enterpriseMenu.map((item) => (
                    <Link key={item.name} to={item.path} className={`block py-2 text-sm nav-link ${isActive(item.path) ? 'text-primary' : ''}`}>
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/all-solutions" className={`block py-2 nav-link ${isActive('/all-solutions') ? 'text-primary' : ''}`}>All Solutions</Link>
            <Link to="/home-internet" className={`block py-2 nav-link ${isActive('/home-internet') ? 'text-primary' : ''}`}>Home Internet</Link>

            <a
              href="tel:+15128595047"
              className="flex items-center justify-center gap-2 w-full py-3 font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors rounded-full mt-4 shadow-sm"
              aria-label="Call SimNet at (512) 859-5047"
            >
              <Phone className="h-4 w-4" />
              (512) 859-5047
            </a>
          </div>
        )}
      </nav>
    </header>
  );
};
